import {
  Box,
  Grid2 as Grid,
  IconButton,
  styled,
  Typography,
  Chip
} from '@mui/material'
import ButtonBase from 'design-system/button'
import NavLinkBase from 'design-system/NavLink'
import { ADD_ESTABLISHMENT, EDIT_ESTABLISHMENT } from 'navigation/CONSTANTS'
import React, { useState } from 'react'
import { useList } from '../../hooks'
import { GridColDef } from '@mui/x-data-grid'
import { IEstablishmentResponse, IModulePermissionType } from 'types'
import AddIcon from '@mui/icons-material/Add'
import { Edit as EditIcon, Settings as SettingsIcon } from '@mui/icons-material'
import { HEALTHCARE_REGISTRATION } from 'services/establishment/CONSTANTS'
import PermissionModal, {
  AVAILABLE_MODULES
} from './components/PermissionModal'
import DataGridBase from 'design-system/DataGrid'

const StyledDataGrid = styled(DataGridBase<IEstablishmentResponse>)(
  ({ theme }) => ({
    '& .MuiDataGrid-topContainer': {
      '& [role="row"]': {
        backgroundColor: theme.palette.primarySoft.main
      },
      '&, & svg': {
        color: theme.palette.primarySoft.contrastText
      }
    }
  })
)

const columnsGuide: GridColDef<IEstablishmentResponse>[] = [
  {
    field: 'establishment_name',
    headerName: 'Razão social',
    sortable: true,
    flex: 3
  },
  {
    field: 'email',
    headerName: 'Email',
    sortable: true,
    flex: 4
  },
  {
    field: 'modules_permission',
    headerName: 'Módulos Permitidos',
    sortable: false,
    filterable: false,
    renderCell: params => (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          height: '100%',
          padding: '4px 0'
        }}
      >
        {params.row.modules_permission?.map(module => {
          const moduleInfo = AVAILABLE_MODULES.find(m => m.id === module)
          return (
            <Chip
              key={module}
              label={moduleInfo?.name || module}
              size='small'
              color={moduleInfo?.color}
            />
          )
        })}
      </Box>
    ),
    flex: 8
  },
  {
    field: 'active',
    headerName: 'Status',
    sortable: false,
    filterable: false,
    valueFormatter: params => (params ? 'Ativo' : 'Inativo'),
    width: 100
  }
]

const EstablishmentPage: React.FC = () => {
  const [permissionModalOpen, setPermissionModalOpen] = useState(false)
  const [selectedEstablishment, setSelectedEstablishment] =
    useState<IEstablishmentResponse | null>(null)

  const { handleEdit, list, state } = useList<IEstablishmentResponse>({
    api: HEALTHCARE_REGISTRATION,
    editPage: item => EDIT_ESTABLISHMENT(item.toString())
  })

  const [localList, setLocalList] = useState<IEstablishmentResponse[]>([])

  React.useEffect(() => {
    setLocalList(list)
  }, [list])

  const handlePermissionEdit = (id: any, row: IEstablishmentResponse) => {
    setSelectedEstablishment(row)
    setPermissionModalOpen(true)
  }

  const handleConfirmPermissions = (modules: IModulePermissionType[]) => {
    if (selectedEstablishment) {
      const updatedEstablishment = {
        ...selectedEstablishment,
        modules_permission: modules
      }
      setSelectedEstablishment(null)

      setLocalList(prevList =>
        prevList.map(establishment =>
          establishment.establishment_id ===
          selectedEstablishment.establishment_id
            ? updatedEstablishment
            : establishment
        )
      )
    }

    setPermissionModalOpen(false)
  }
  const columns: GridColDef<IEstablishmentResponse>[] = [
    ...columnsGuide,
    {
      field: 'settings',
      headerName: 'Ações',
      sortable: false,
      filterable: false,
      renderCell: params => (
        <IconButton onClick={() => handleEdit(params.id, params.row)}>
          <EditIcon />
        </IconButton>
      ),
      width: 100
    },
    {
      field: 'permissions',
      headerName: 'Gerenciar',
      sortable: false,
      filterable: false,
      renderCell: params => (
        <IconButton onClick={() => handlePermissionEdit(params.id, params.row)}>
          <SettingsIcon />
        </IconButton>
      ),
      width: 100
    }
  ]

  return (
    <Box display={'flex'} sx={{ justifyContent: 'center' }}>
      <Grid container spacing={2} flex={1} maxWidth={'1200px'} p={2}>
        <Grid
          container
          size={12}
          spacing={1}
          justifyContent='space-between'
          alignItems={'center'}
        >
          <Typography variant='h5'>Estabelecimentos</Typography>
          <NavLinkBase to={ADD_ESTABLISHMENT} absolute>
            <ButtonBase
              sx={{
                '& .MuiButton-icon': {
                  marginLeft: '0px'
                }
              }}
              endIcon={<AddIcon />}
            >
              <Typography variant='body1'>Adicionar estabelecimento</Typography>
            </ButtonBase>
          </NavLinkBase>
        </Grid>
        <Grid flex={1}>
          <StyledDataGrid
            rows={localList}
            columns={columns}
            getRowId={row => row.establishment_id}
            loading={state == 'loading'}
            disableRowSelectionOnClick
            disableColumnMenu
            rowHeight={80}
            pageSizeOptions={[25, 50, 100]}
            slotProps={{
              pagination: {
                labelDisplayedRows: ({ from, to, count }) =>
                  `Exibindo ${from}–${to} de ${count} registros`
              },
              noRowsOverlay: {
                'aria-label': 'Nenhum estabelecimento encontrado'
              }
            }}
          />
        </Grid>
        <PermissionModal
          permissionModalOpen={permissionModalOpen}
          selectedEstablishment={selectedEstablishment}
          onConfirm={handleConfirmPermissions}
          onClose={() => setPermissionModalOpen(false)}
        />
      </Grid>
    </Box>
  )
}

export default EstablishmentPage
